import { getApiUrl } from '../config/api';

// Define the types locally to avoid dependency issues
interface TrackActivationResponse {
  success: boolean;
  message: string;
  activeTrackId: string;
}

interface AudioTrack {
  id: string;
  name: string;
  url: string;
  audioBuffer: AudioBuffer | null;
  startTime: number;
  duration: number;
  originalDuration: number;
  volume: number;
  isMuted: boolean;
  color: string;
  laneIndex: number;
  fadeInDuration: number;
  fadeOutDuration: number;
  trimStart: number;
  trimEnd: number;
  isSelected?: boolean;
  isActive?: boolean; // NEW: Lazy loading state
}

interface ActiveTrackResponse {
  activeTrack: AudioTrack | null;
}

// Helper function to get authentication headers
const getAuthHeaders = (additionalHeaders: Record<string, string> = {}): Record<string, string> => {
  const token = localStorage.getItem('authToken');
  return {
    'Content-Type': 'application/json',
    ...additionalHeaders,
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

/**
 * ✅ NEW: Track activation service for lazy loading
 * Handles track activation/deactivation for memory management
 */
export class TrackActivationService {
  
  /**
   * Activate a track (deactivates all others in project)
   */
  static async activateTrack(trackId: string): Promise<TrackActivationResponse> {
    console.log(`🎯 Activating track: ${trackId}`);
    
    const response = await fetch(getApiUrl(`/music-generation/activate-track/${trackId}`), {
      method: 'POST',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to activate track: ${response.statusText} - ${errorData.message || 'Unknown error'}`);
    }

    const data: TrackActivationResponse = await response.json();
    console.log(`✅ Track ${trackId} activated successfully`);
    return data;
  }

  /**
   * Get the currently active track for a project
   */
  static async getActiveTrack(projectId: string): Promise<ActiveTrackResponse> {
    console.log(`🔍 Getting active track for project: ${projectId}`);
    
    const response = await fetch(getApiUrl(`/music-generation/active-track/${projectId}`), {
      method: 'GET',
      headers: getAuthHeaders(),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(`Failed to get active track: ${response.statusText} - ${errorData.message || 'Unknown error'}`);
    }

    const data: ActiveTrackResponse = await response.json();
    console.log(`📥 Active track retrieved:`, data.activeTrack?.id || 'None');
    return data;
  }
}

// Export types for use in components
export type { TrackActivationResponse, ActiveTrackResponse };
